import React from "react";
import { X, MapPin, ArrowRight } from "lucide-react";

/**
 * iter388 — MarketHintToast
 *
 * First-visit soft hint shown on /pricing when the auto-resolved regional
 * market is non-EU. Increases transparency (the user sees WHY they see these
 * prices) and conversion (the user does not feel "trapped" in unexpected
 * pricing — they can switch any time).
 *
 * Behaviour:
 *  - Renders only if market ∈ { t1, bg, t3 } (EU is the silent default)
 *  - Dismisses persistently via localStorage key `nutramilo_market_hint_dismissed_v1`
 *  - Auto-hides after 12 s (also dismissible by button)
 *  - "Switch region" CTA scrolls the page to the MarketBadge selector
 *
 * Currency conversion rates are static approximations (Feb 2026) — not used
 * for billing, only for the UX hint string. Real billing always happens in EUR.
 */

const STORAGE_KEY = "nutramilo_market_hint_dismissed_v1";
const AUTO_HIDE_MS = 12000;

// Per-market hint configuration. Sample price uses the Pro tier (the most
// commonly viewed plan) to give the user a tangible reference point.
const MARKET_HINT = {
  t1: {
    bg: {
      headline: "Показват се премиум цени",
      sub: "Pro: $29.90 ≈ €29.90. Може да превключиш регион по всяко време.",
    },
    en: {
      headline: "Showing premium-region prices",
      sub: "Pro: $29.90 ≈ €29.90. Switch region anytime.",
    },
    es: {
  headline: "Mostrando precios de la región premium",
  sub: "Pro: $29.90 ≈ €29.90. Cambia de región en cualquier momento.",
},
  },
  bg: {
    bg: {
      headline: "Показват се локални цени",
      sub: "Pro: 32 лв ≈ €19.90. Може да превключиш регион по всяко време.",
    },
    en: {
      headline: "Showing local prices",
      sub: "Pro: 32 BGN ≈ €19.90. Switch region anytime.",
    },
  },
  t3: {
    bg: {
      headline: "Достъпни цени за вашия регион",
      sub: "Pro: ≈ ₹999 / ≈ R$60 (€11.90). Може да превключиш регион по всяко време.",
    },
    en: {
      headline: "Designed for emerging markets",
      sub: "Pro: ≈ ₹999 / ≈ R$60 (€11.90). Switch region anytime.",
    },
  },
};

export default function MarketHintToast({ market, language = "en", isDark = false }) {
  const [visible, setVisible] = React.useState(false);

  React.useEffect(() => {
    if (!market || market === "eu") return;
    if (!MARKET_HINT[market]) return;
    let dismissed = false;
    try {
      dismissed = localStorage.getItem(STORAGE_KEY) === "1";
    } catch (_e) { /* incognito / disabled storage */ }
    if (dismissed) return;
    setVisible(true);
    const timer = setTimeout(() => setVisible(false), AUTO_HIDE_MS);
    return () => clearTimeout(timer);
  }, [market]);

  const dismiss = React.useCallback(() => {
    setVisible(false);
    try {
      localStorage.setItem(STORAGE_KEY, "1");
    } catch (_e) { /* ignore */ }
  }, []);

  const handleSwitchRegion = React.useCallback(() => {
    const badge = document.querySelector("[data-testid='pricing-market-badge']");
    if (badge && typeof badge.scrollIntoView === "function") {
      badge.scrollIntoView({ behavior: "smooth", block: "center" });
    }
    const selector = document.querySelector("[data-testid='pricing-market-selector']");
    if (selector) {
      setTimeout(() => { try { selector.focus(); } catch (_e) { /* ignore */ } }, 500);
    }
    dismiss();
  }, [dismiss]);

  if (!visible || !market || !MARKET_HINT[market]) return null;
  const meta = MARKET_HINT[market][language] || MARKET_HINT[market].en;

  return (
    <div
      data-testid="pricing-market-hint-toast"
      className="fixed bottom-4 right-4 left-4 sm:left-auto sm:max-w-sm z-50 rounded-xl shadow-lg border p-4"
      style={{
        background: isDark ? "rgba(15, 23, 42, 0.97)" : "#ffffff",
        borderColor: isDark ? "rgba(148, 163, 184, 0.25)" : "#E5E7EB",
        boxShadow: isDark
          ? "0 8px 28px rgba(0,0,0,0.5)"
          : "0 8px 28px rgba(15, 23, 42, 0.12)",
      }}
    >
      <div className="flex items-start gap-3">
        <div
          className="rounded-lg flex-shrink-0 w-9 h-9 flex items-center justify-center"
          style={{
            background: "rgba(14, 165, 233, 0.12)",
            color: "#0EA5E9",
          }}
        >
          <MapPin className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div
            className="text-sm font-semibold mb-0.5"
            data-testid="pricing-market-hint-headline"
            style={{ color: isDark ? "#f1f5f9" : "#1A1A2E" }}
          >
            {meta.headline}
          </div>
          <div
            className="text-xs leading-relaxed"
            data-testid="pricing-market-hint-sub"
            style={{ color: isDark ? "#94a3b8" : "#64748B" }}
          >
            {meta.sub}
          </div>
          <button
            type="button"
            data-testid="pricing-market-hint-switch"
            onClick={handleSwitchRegion}
            className="inline-flex items-center gap-1 mt-2 text-xs font-medium"
            style={{ color: "#0EA5E9", cursor: "pointer", background: "transparent", border: "none", padding: 0 }}
          >
            {language === "bg" ? "Превключи регион" : "Switch region"}
            <ArrowRight className="w-3 h-3" />
          </button>
        </div>
        <button
          type="button"
          data-testid="pricing-market-hint-dismiss"
          onClick={dismiss}
          aria-label="dismiss"
          className="flex-shrink-0 -mt-1 -mr-1 p-1 rounded"
          style={{
            color: isDark ? "#64748B" : "#94A3B8",
            background: "transparent",
            border: "none",
            cursor: "pointer",
          }}
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
